import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import usePermisos from '../hooks/usePermisos';

const MODULES = [
    { key: 'dashboard',   label: 'Dashboard'    },
    { key: 'usuarios',    label: 'Usuarios'     },
    { key: 'roles',       label: 'Roles'        },
    { key: 'settings',    label: 'Configuración'},
    { key: 'clientes',    label: 'Clientes'     },
    { key: 'pqr',         label: 'PQR'          },
    { key: 'facturacion', label: 'Facturación'  },
    { key: 'reportes',    label: 'Reportes'     },
];

const ACTIONS = ['create', 'read', 'update', 'delete'];
const ACTION_LABELS = { create: 'Crear', read: 'Ver', update: 'Editar', delete: 'Eliminar' };

const Roles = () => {
    const { can, loaded } = usePermisos();
    const [roles, setRoles] = useState([]);
    const [permisos, setPermisos] = useState([]);
    const [selected, setSelected] = useState(null);
    const [rolePermisos, setRolePermisos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState({ nombre: '', slug: '', descripcion: '' });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [rolesRes, permisosRes] = await Promise.all([
                api.get('/roles'),
                api.get('/roles/permisos')
            ]);
            setRoles(rolesRes.data);
            setPermisos(permisosRes.data);
        } catch {
            setError('Error cargando datos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { 
        if (loaded) loadData(); 
    }, [loadData, loaded]);

    const selectRole = async (role) => {
        setSelected(role);
        setError('');
        setSuccess('');
        try {
            const { data } = await api.get(`/roles/${role.id}`);
            setRolePermisos(data.permisos.map(p => p.id));
        } catch {
            setRolePermisos([]);
        }
    };

    const togglePermiso = (permisoId) => {
        setRolePermisos(prev =>
            prev.includes(permisoId)
                ? prev.filter(id => id !== permisoId)
                : [...prev, permisoId]
        );
    };

    const toggleModulo = (modulo) => {
        const moduloPermisos = permisos.filter(p => p.modulo === modulo).map(p => p.id);
        const allSelected = moduloPermisos.every(id => rolePermisos.includes(id));
        if (allSelected) {
            setRolePermisos(prev => prev.filter(id => !moduloPermisos.includes(id)));
        } else {
            setRolePermisos(prev => [...new Set([...prev, ...moduloPermisos])]);
        }
    };

    const savePermisos = async () => {
        if (!selected) return;
        setSaving(true);
        setError('');
        try {
            await api.put(`/roles/${selected.id}/permisos`, { permiso_ids: rolePermisos });
            setSuccess('Permisos guardados correctamente');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Error guardando permisos');
        } finally {
            setSaving(false);
        }
    };

    const createRole = async () => {
        setSaving(true);
        setError('');
        try {
            await api.post('/roles', form);
            setModalOpen(false);
            setForm({ nombre: '', slug: '', descripcion: '' });
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error creando rol');
        } finally {
            setSaving(false);
        }
    };

    const deleteRole = async (role) => {
        if (role.es_sistema) return;
        if (!window.confirm(`¿Eliminar el rol "${role.nombre}"?`)) return;
        try {
            await api.delete(`/roles/${role.id}`);
            if (selected?.id === role.id) setSelected(null);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error eliminando rol');
        }
    };

    const restoreDefaults = async () => {
        if (!window.confirm('¿Restaurar todos los permisos a los valores por defecto? Esto sobreescribirá la configuración actual.')) return;
        try {
            await api.post('/roles/restore-defaults');
            setSuccess('Permisos restaurados a valores por defecto');
            if (selected) selectRole(selected);
            setTimeout(() => setSuccess(''), 4000);
        } catch (err) {
            setError(err.response?.data?.error || 'Error restaurando defaults');
        }
    };

    const slugify = (text) => text.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

    const getPermisoId = (modulo, accion) => {
        return permisos.find(p => p.modulo === modulo && p.accion === accion)?.id;
    };

    const isModuloAllSelected = (modulo) => {
        const moduloPermisos = permisos.filter(p => p.modulo === modulo).map(p => p.id);
        return moduloPermisos.length > 0 && moduloPermisos.every(id => rolePermisos.includes(id));
    };

    return (
        <div className="flex gap-6 h-full">
            {/* Lista de roles */}
            <div className="w-72 flex-shrink-0">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Roles</h2>
                    <div className="flex gap-2">
                    {can('roles', 'update') && (
                    <button
                        onClick={restoreDefaults}
                        className="text-gray-600 text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50"
                    >
                        ↺ Defaults
                    </button>
                    )}
                    {can('roles', 'create') && (
                    <button
                        onClick={() => { setModalOpen(true); setError(''); }}
                        className="text-white text-xs font-semibold px-3 py-1.5 rounded-lg"
                        style={{ backgroundColor: 'var(--color-primary)' }}
                    >
                        + Nuevo
                    </button>
                    )}
                    </div>
                </div>

                {error && <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-lg px-3 py-2 mb-3">{error}</div>}
                {success && <div className="bg-green-50 border border-green-200 text-green-700 text-xs rounded-lg px-3 py-2 mb-3">{success}</div>}

                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                    {loading ? (
                        <div className="p-4 text-center text-gray-400 text-sm">Cargando...</div>
                    ) : (
                        roles.map(role => (
                            <div
                                key={role.id}
                                onClick={() => selectRole(role)}
                                className={`flex items-center justify-between px-4 py-3 cursor-pointer border-b border-gray-50 last:border-0 transition-colors ${
                                    selected?.id === role.id ? 'bg-blue-50' : 'hover:bg-gray-50'
                                }`}
                            >
                                <div>
                                    <p className={`text-sm font-medium ${selected?.id === role.id ? 'text-blue-700' : 'text-gray-800'}`}>
                                        {role.nombre}
                                    </p>
                                    <p className="text-xs text-gray-400">{role.slug}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    {role.es_sistema ? (
                                        <span className="text-xs text-gray-400">Sistema</span>
                                    ) : can('roles', 'delete') ? (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); deleteRole(role); }}
                                            className="text-red-400 hover:text-red-600 text-xs"
                                        >
                                            ✕
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Matriz de permisos */}
            <div className="flex-1">
                {!selected ? (
                    <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-gray-400">
                        <p className="text-4xl mb-3">🔐</p>
                        <p>Selecciona un rol para configurar sus permisos</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <div>
                                <h3 className="text-lg font-bold text-gray-800">{selected.nombre}</h3>
                                <p className="text-xs text-gray-400">{selected.slug}</p>
                            </div>
                            {selected.slug !== 'superadmin' && can('roles', 'update') && (
                                <button
                                    onClick={savePermisos}
                                    disabled={saving}
                                    className="text-white text-sm font-semibold px-5 py-2 rounded-lg disabled:opacity-60"
                                    style={{ backgroundColor: 'var(--color-primary)' }}
                                >
                                    {saving ? 'Guardando...' : 'Guardar permisos'}
                                </button>
                            )}
                        </div>

                        {selected.slug === 'superadmin' ? (
                            <div className="p-6 text-center text-gray-500 text-sm">
                                El Superadmin tiene acceso total al sistema y no requiere configuración de permisos.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-48">Módulo</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Todo</th>
                                            {ACTIONS.map(a => (
                                                <th key={a} className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">
                                                    {ACTION_LABELS[a]}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {MODULES.map(mod => (
                                            <tr key={mod.key} className="hover:bg-gray-50">
                                                <td className="px-6 py-3 font-medium text-gray-700">{mod.label}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={isModuloAllSelected(mod.key)}
                                                        onChange={() => toggleModulo(mod.key)}
                                                        className="w-4 h-4 cursor-pointer"
                                                        style={{ accentColor: 'var(--color-primary)' }}
                                                    />
                                                </td>
                                                {ACTIONS.map(accion => {
                                                    const pid = getPermisoId(mod.key, accion);
                                                    return (
                                                        <td key={accion} className="px-4 py-3 text-center">
                                                            {pid ? (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={rolePermisos.includes(pid)}
                                                                    onChange={() => togglePermiso(pid)}
                                                                    className="w-4 h-4 cursor-pointer"
                                                                    style={{ accentColor: 'var(--color-primary)' }}
                                                                />
                                                            ) : (
                                                                <span className="text-gray-200">—</span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Modal nuevo rol */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">Nuevo rol</h3>
                            <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-6 space-y-4">
                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                                <input
                                    value={form.nombre}
                                    onChange={e => setForm(prev => ({ ...prev, nombre: e.target.value, slug: slugify(e.target.value) }))}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    placeholder="ej: Supervisor"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                                <input
                                    value={form.slug}
                                    onChange={e => setForm(prev => ({ ...prev, slug: slugify(e.target.value) }))}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none bg-gray-50"
                                    placeholder="ej: supervisor"
                                />
                                <p className="text-xs text-gray-400 mt-1">Se genera automáticamente del nombre</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                                <input
                                    value={form.descripcion}
                                    onChange={e => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    placeholder="Descripción del rol"
                                />
                            </div>
                        </div>
                        <div className="px-6 pb-6 flex gap-3">
                            <button
                                onClick={() => setModalOpen(false)}
                                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={createRole}
                                disabled={saving}
                                className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}
                            >
                                {saving ? 'Creando...' : 'Crear rol'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Roles;
